import { Box3, BoxGeometry, DoubleSide, IcosahedronGeometry, Mesh, MeshBasicMaterial, MeshPhongMaterial, RepeatWrapping, Texture, Vector3 } from 'three';
import { Loader3d } from '../utils/loader3d';
import { Scene3DConfig, CellGroup, RowGroup, ColumnGroup, DimGroup } from './scene3DConfig';
import { Scene3DMouse } from './scene3DMouse';
import { BaseClass } from 'dac-web-base';
import { Object3D } from './Object3d';
import { Loading } from '../../loading/loading';
import { Utils3d } from '../utils/utils3d';

export class Scene3DUnit extends BaseClass {
	constructor( renderUnit ) {
		super();
		this.scene = null;
		this.controls = null;
		this.camera = null;
		this._gridData = {
			cells: new CellGroup(),
			rows: new RowGroup(),
			columns: new ColumnGroup(),
			dimensions: new DimGroup()
		};
		this.renderUnit = renderUnit;
		this.enabled = false;

		this._loader = new Loader3d();
		this._loading = new Loading();
		this._objects = [];
		this._sceneObjects = {
			floor: null
		};
	}

	async init( grid, container ) {

		this.container = container;
		this.grid = grid;

		this._backendUrl = sessionStorage.getItem( 'backendUrl' );

		// init scene config
		new Scene3DConfig().init( this );
		
		// init mouse events
		this._sceneMouse = new Scene3DMouse();
		const mouse = this._sceneMouse.init( this, container );
		mouse.subscribe( 'onObjectClicked', this.#onObjectClicked.bind( this ) );
		mouse.subscribe( 'onObjectDblClicked', this.#onObjectDblClicked.bind( this ) );
		mouse.subscribe( 'onFloorClicked', this.#onFloorClicked.bind( this ) );
		mouse.subscribe( 'onObjectHover', this.#onObjectHover.bind( this ) );
		mouse.subscribe( 'onObjectDrag', this.#onObjectDrag.bind( this ) );
	}
	
	setEnabled( enabled ) {
		this.enabled = enabled;

		if( !this._sceneMouse ) return;

		this._sceneMouse.setEnabled( enabled );
	}

	async initScene( currentData, aquaGrid ) {

		//remove objects
		for( let i = 0; i < this._objects.length; i++ ) {
			this._objects[i].clearMesh();
		}
		this._objects = [];

		//init grid
		await this.initGrid( aquaGrid, currentData );
	}

	async initGrid( aquaGrid, currentData ) {
		
		this._loading.show();

		//prepare scene: add floor, walls, etc
		const gridBox = this.#getGridSize( aquaGrid );
		await this.#addFloor( gridBox.size );
		await this.#addEnvironment();

		//prepare general data
		const data = this.#getGeneralData( aquaGrid, currentData );

		//call when answers change
		const respuestas = currentData.respuestas;
		for( let i = 0; i < respuestas.length; i++ ) {
			const answer = respuestas[i];

			if( !answer.objetos_3_ds ) continue;
			for( let j = 0; j < answer.objetos_3_ds.length; j++ ) {
				const object = answer.objetos_3_ds[j];
				const obj3d = new Object3D( this.scene, data );

				//draw object
				await obj3d.draw( object, answer );

				this._objects.push( obj3d );
			}
		}
		
		this._loading.hide();
	}

	async #onObjectClicked( data ) {
	}

	async #onObjectDblClicked( data ){
	}

	async #onObjectDrag( data ) {
	}

	async #onFloorClicked() {
	}

	#onObjectHover( data ) {
	}

	#getGeneralData( aquaGrid, currentData ) {
		const generalData = {
			grid: aquaGrid,
			currentData: currentData
		};

		//Altura del invernadero ( hasta los canalones )
		const respuesta = currentData.respuestas.find( r => r.DETOPPTOpcion == 'HAUTCHE' );
		if( respuesta ) generalData.alturaInvernadero = parseFloat( respuesta.DETOPT3DAltura );

		//TODO: remove this when answers have the real data
		generalData.alturaInvernadero = 3;

		return generalData;
	}

	#getGridSize( aquaGrid ) {
		const box = {
			max: new Vector3(-Infinity,-Infinity,-Infinity),
			min: new Vector3( Infinity, Infinity, Infinity )
		};
		
		for( let i = 0; i < aquaGrid.cells.length; i++ ) {
			const cell = aquaGrid.cells[i];
			const cellMax = new Vector3( cell.position.x + cell.MIDAncho / 2.0, cell.position.y, cell.position.z + cell.MIDLargo / 2.0 );
			const cellMin = new Vector3( cell.position.x - cell.MIDAncho / 2.0, cell.position.y, cell.position.z - cell.MIDLargo / 2.0 );
			box.max.x = Math.max( box.max.x, cellMax.x );
			box.max.y = Math.max( box.max.y, cellMax.y );
			box.max.z = Math.max( box.max.z, cellMax.z );
			box.min.x = Math.min( box.min.x, cellMin.x );
			box.min.y = Math.min( box.min.y, cellMin.y );
			box.min.z = Math.min( box.min.z, cellMin.z );
		}

		box.size = new Vector3().subVectors( box.max, box.min );

		return box;
	}

	async #addFloor( size ) {
		
		//remove if exists
		if( this._sceneObjects.floor ) {
			this.scene.remove( this._sceneObjects.floor );
			Utils3d.dispose3d( this._sceneObjects.floor );
			this._sceneObjects.floor = null;
		}

		const fGeo = new BoxGeometry( size.x, size.z, 0.125, 1,1 );
		fGeo.rotateX( - Math.PI / 2 );
		fGeo.translate( 0, -0.075, 0 );
		const mat = new MeshPhongMaterial();
		if( mat.map ) {
			mat.map.repeat.set( size.x / 10, size.z / 10 );
			mat.map.wrapS = mat.map.wrapT = RepeatWrapping;
		}

		this._sceneObjects.floor = new Floor( fGeo, mat );
		this.scene.add( this._sceneObjects.floor );
	}

	// create a sphere with an environment map as a texture
	#addEnvironment() {
		if( !this.environment ) {
			let gradTexture = ( color ) => {
				var c = document.createElement( 'canvas' );
				var ct = c.getContext( '2d' );
				var size = 1024;
				c.width = 16; c.height = size;
				var gradient = ct.createLinearGradient( 0, 0, 0, size );
				var i = color[0].length;
				while ( i-- ) { gradient.addColorStop( color[0][i], color[1][i] ); }
				ct.fillStyle = gradient;
				ct.fillRect( 0, 0, 16, size );
				var texture = new Texture( c );
				texture.needsUpdate = true;
				return texture;
			};

			this.environment = new Background( new IcosahedronGeometry( this.camera.far * 0.75, 2 ),
				new MeshBasicMaterial( {
					map: gradTexture( [ [ 0.75, 0.6, 0.4, 0.25 ], [ '#1B1D1E', '#3D4143', '#72797D', '#b0babf' ] ] ),
					side: DoubleSide,
					depthWrite: false,
					fog: false
				} ) );

			this.environment.initial = true;

			//add so mouse always collision with something
			//this.object3dInteraction.selection.addSelectableObject( this.environment );

			this.scene.add( this.environment );
		}

	}

}

class Floor extends Mesh { constructor( geometry, material ) { super( geometry, material ); this.name = 'Floor'; } }
class Background extends Mesh { constructor( geometry, material ) { super( geometry, material ); this.name = 'Background'; } }